import React, { useState } from "react";
import { quickScoreMatch } from "../lib/api";

/**
 * QuickScoreModal — 1-tap final-score entry. Owners/captains of either side
 * can report. Auto-decides the winner from the higher score. Lightweight
 * cousin of MatchResultModal (no K/D/A, no video) so dashboard scoring is
 * literally three clicks: open → +1 → submit.
 */
export function QuickScoreModal({ match, onClose, onToast, onScored }) {
  const teamA = match?.from_team?.name || "Team A";
  const teamB = match?.to_team?.name || "Team B";
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [busy, setBusy] = useState(false);

  if (!match) return null;

  const submit = async () => {
    if (scoreA === scoreB) {
      onToast?.("One team must win — adjust the score.");
      return;
    }
    setBusy(true);
    try {
      await quickScoreMatch(match.challenge_id, scoreA, scoreB);
      onToast?.(`🏆 Score recorded — ${scoreA > scoreB ? teamA : teamB} wins.`);
      onScored?.();
      onClose?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't record score.");
    } finally { setBusy(false); }
  };

  const ScoreColumn = ({ label, color, val, setVal, testId }) => (
    <div className="flex flex-col items-center gap-2 flex-1" data-testid={`quick-score-col-${testId}`}>
      <p className={`text-[10px] tracking-[3px] font-bold ${color}`}>{label}</p>
      <p className="font-display text-5xl text-white tabular-nums" data-testid={`quick-score-${testId}`}>
        {val}
      </p>
      <div className="flex gap-1">
        <button type="button" onClick={() => setVal(Math.max(0, val - 1))}
                data-testid={`quick-score-${testId}-minus`}
                className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white text-lg">
          <i className="fas fa-minus text-xs" />
        </button>
        <button type="button" onClick={() => setVal(Math.min(99, val + 1))}
                data-testid={`quick-score-${testId}-plus`}
                className="w-9 h-9 rounded-lg bg-[#14b8a6]/20 hover:bg-[#14b8a6]/35 text-[#14b8a6] text-lg">
          <i className="fas fa-plus text-xs" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-4 flex items-center justify-center"
         onClick={onClose} data-testid="quick-score-backdrop">
      <div className="glass neon-glow-cyan rounded-3xl p-6 w-full max-w-md"
           onClick={(e) => e.stopPropagation()}
           data-testid="quick-score-modal">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-bolt mr-1" />QUICK SCORE
            </p>
            <h2 className="font-display text-2xl grad-text">Report Result</h2>
          </div>
          <button onClick={onClose} data-testid="quick-score-close"
                  className="text-white/60 hover:text-white text-2xl">
            <i className="fas fa-times" />
          </button>
        </div>
        <p className="text-[11px] text-white/45 mb-5">
          One tap per goal/round. Winner is auto-detected.
        </p>

        <div className="flex items-center gap-2">
          <ScoreColumn label={teamA.toUpperCase()} color="text-[#14b8a6]"
                       val={scoreA} setVal={setScoreA} testId="a" />
          <p className="font-display text-2xl text-white/30">VS</p>
          <ScoreColumn label={teamB.toUpperCase()} color="text-[#14b8a6]"
                       val={scoreB} setVal={setScoreB} testId="b" />
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  data-testid="quick-score-cancel"
                  className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost">
            CANCEL
          </button>
          <button type="button" onClick={submit} disabled={busy || scoreA === scoreB}
                  data-testid="quick-score-submit"
                  className="text-[11px] tracking-widest px-5 py-2 rounded-xl btn-neon disabled:opacity-50">
            {busy ? "..." : "SUBMIT SCORE"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickScoreModal;
